import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, PackageX } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function InventoryAttentionPanel({ onOpenItem }) {
  const [attention, setAttention] = useState(null);
  const [showAllLowStock, setShowAllLowStock] = useState(false);
  const [selectedLowStock, setSelectedLowStock] = useState([]);
  const [addingToBuy, setAddingToBuy] = useState(false);

  const load = useCallback(() => {
    api
      .get("/inventory/attention")
      .then((response) => setAttention(response.data))
      .catch((error) => toast.error(errMsg(error)));
  }, []);
  useEffect(() => {
    load();
    window.addEventListener("ask-jonny:inventory-updated", load);
    return () => window.removeEventListener("ask-jonny:inventory-updated", load);
  }, [load]);

  const openAllLowStock = () => {
    setSelectedLowStock([]);
    setShowAllLowStock(true);
  };
  const toggleLowStock = (id) => {
    setSelectedLowStock((current) => (
      current.includes(id)
        ? current.filter((itemId) => itemId !== id)
        : [...current, id]
    ));
  };
  const addSelectedToBuy = async () => {
    if (!selectedLowStock.length) {
      return;
    }
    setAddingToBuy(true);
    try {
      const { data } = await api.post("/tobuy/from-low-stock", {
        inventory_ids: selectedLowStock,
      });
      toast.success(
        `${data.created} item${data.created === 1 ? "" : "s"} added to To buy`
        + (data.skipped ? ` · ${data.skipped} already listed or no longer low` : ""),
      );
      setShowAllLowStock(false);
      setSelectedLowStock([]);
      load();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setAddingToBuy(false);
    }
  };

  if (!attention) {
    return (
      <div
        className="mb-6 grid gap-3 sm:grid-cols-2"
        data-testid="inventory-attention-loading"
      >
        <div className="h-28 animate-pulse rounded-xl border bg-muted/40" />
        <div className="h-28 animate-pulse rounded-xl border bg-muted/40" />
      </div>
    );
  }

  return (
    <section className="mb-6 grid gap-3 sm:grid-cols-2" data-testid="inventory-attention-panel">
      <div className="rounded-xl border bg-card p-4" data-testid="inventory-shortages-panel">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          Recorded shortages
        </h2>
        {attention.shortages.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="inventory-shortages-empty">
            None recorded.
          </p>
        ) : (
          <div className="space-y-1">
            {attention.shortages.map((shortage) => (
              <Link
                key={shortage.id}
                to={`/prep?booking=${shortage.booking_id}`}
                className="block text-xs transition-colors hover:text-primary hover:underline"
                data-testid={`inventory-shortage-link-${shortage.id}`}
              >
                {shortage.reference}: {shortage.qty} × {shortage.item}
              </Link>
            ))}
          </div>
        )}
      </div>
      <div className="rounded-xl border bg-card p-4" data-testid="inventory-low-stock-panel">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
          <PackageX className="h-4 w-4 text-rose-500" />
          Low recorded stock
        </h2>
        {attention.low_stock.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="inventory-low-stock-empty">
            All items above minimum.
          </p>
        ) : (
          <>
          <div className="space-y-1">
            {attention.low_stock.slice(0, 3).map((item) => (
              <button
                key={item.id}
                type="button"
                className="flex w-full items-center justify-between gap-3 py-1 text-left text-xs transition-colors hover:text-primary"
                onClick={() => onOpenItem(item)}
                data-testid={`inventory-low-stock-item-${item.id}`}
              >
                <span className="truncate">{item.name}</span>
                <span className="shrink-0 font-mono">
                  {item.quantity} {item.units}
                </span>
              </button>
            ))}
          </div>
          {attention.low_stock.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 h-auto px-0 text-xs"
              onClick={openAllLowStock}
              data-testid="inventory-low-stock-show-more"
            >
              Show all {attention.low_stock.length} items
            </Button>
          )}
          </>
        )}
      </div>
      <Dialog open={showAllLowStock} onOpenChange={setShowAllLowStock}>
        <DialogContent className="max-w-xl" data-testid="inventory-low-stock-dialog">
          <DialogHeader>
            <DialogTitle>Low recorded stock</DialogTitle>
          </DialogHeader>
          <div className="max-h-[55vh] divide-y overflow-y-auto rounded-lg border">
            {attention.low_stock.map((item) => (
              <label
                key={item.id}
                className="flex cursor-pointer items-center gap-3 p-3 hover:bg-muted/40"
                data-testid={`inventory-low-stock-select-row-${item.id}`}
              >
                <Checkbox
                  checked={selectedLowStock.includes(item.id)}
                  onCheckedChange={() => toggleLowStock(item.id)}
                  data-testid={`inventory-low-stock-select-${item.id}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{item.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {item.quantity} {item.units} recorded · minimum {item.min_stock}
                  </span>
                </span>
              </label>
            ))}
          </div>
          <Button
            onClick={addSelectedToBuy}
            disabled={addingToBuy || selectedLowStock.length === 0}
            data-testid="inventory-low-stock-add-to-buy"
          >
            {addingToBuy
              ? "Adding to To buy…"
              : `Add ${selectedLowStock.length || "selected"} to To buy`}
          </Button>
        </DialogContent>
      </Dialog>
    </section>
  );
}